/* eslint-disable no-restricted-syntax */
/**
 * A waypoint image.
 */
class WaypointImage {
  private _width: number
  private _height: number
  private _src: string
  private _binary: Uint8ClampedArray

  /**
   * Initializes a waypoint image.
   *
   * @constructor
   * @param width - The image width in pixels.
   * @param height - The image height in pixels.
   * @param src - The image source.
   * @param binary - The image binary.
   */
  constructor(
    width: number,
    height: number,
    src: string,
    binary: Uint8ClampedArray
  ) {
    this._width = width
    this._height = height
    this._src = src
    this._binary = binary
  }

  /**
   * Gets the image source.
   */
  get src(): string {
    return this._src
  }

  /**
   * Gets the image height in pixels.
   */
  get height(): number {
    return this._height
  }

  /**
   * Gets the image width in pixels.
   */
  get width(): number {
    return this._width
  }

  /**
   * Gets the image binary.
   */
  get binary(): Uint8ClampedArray {
    return this._binary
  }
}

export default WaypointImage

interface IconMetrics {
  x: number
  y: number
  dx: number
  dy: number
}

/**
 * Formats an icon image ID to be used for Mapbox to look up the icon image.
 * @param icon - Icon name.
 * @param color - Color instance.
 * @param highlighted - Whether the waypoint is highlighted.
 * @param lowZoom - Whether to use the low-zoom variant.
 * @return Formatted waypoint image ID.
 */
export const formatImageId = (
  icon: string,
  color: { toString(): string },
  highlighted: boolean,
  lowZoom?: boolean
): string => {
  return highlighted
    ? `${icon}-${color.toString()}-highlight`
    : `${icon}-${color.toString()}${lowZoom ? '-zoom' : ''}`
}

/**
 * Calculate the correct spacing to draw the icon on the waypoint-base
 *
 * @param icon - The raw svg string
 * @param canvasWidth - The width of the canvas the image is being drawn on
 * @returns IconMetrics with x, y position and dx, dy padding to center the icon
 */
export const getImageSpacing = (
  icon: string,
  canvasWidth: number
): IconMetrics => {
  const doc = new DOMParser().parseFromString(icon, 'image/svg+xml')
  const iconWidth = Number(doc.children[0].getAttribute('width'))
  const iconHeight = Number(doc.children[0].getAttribute('height'))

  // The icon can be at most half the canvas size tall/wide
  const maxIconSize = canvasWidth / 2

  // Calculate the correctly scaled width/height of the icon
  const x =
    iconWidth >= iconHeight
      ? maxIconSize
      : maxIconSize * (iconWidth / iconHeight)
  const y =
    iconHeight >= iconWidth
      ? maxIconSize
      : maxIconSize * (iconHeight / iconWidth)

  // Calculate the padding
  const dx = (canvasWidth - x) / 2
  const dy = (canvasWidth - y) / 2

  return { x, y, dx, dy }
}
